import { useEffect, JSX } from "react";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { BindDialogReturn, DialogProps, DialogResult, DialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { useForm } from "react-hook-form";
import { useUICustomizationSettings } from "../../queries/ui-customization/UseUICustomizationSettings";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import { UICustomizationFieldType } from "../ui-customization-settings/UICustomizationSettingsFieldTypes";
import { customizationSettingsFieldIsDirty, customizationSettingsFieldIsValid, mergeUICustomizationSettings, toUICustomizationField } from "../ui-customization-settings/UICustomizationSettingsUtils";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { useSaveUICustomizationSettings } from "../../queries/ui-customization/UseSaveUICustomizationSettings";
import UICustomizationViewCategoryListSettings from "../ui-customization-settings/UICustomizationViewCategoryListSettings";
import { Button, Divider, Stack } from "@mui/material";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";

export type UICustomizationSettingsDialogInitValues = {
    userID: number;
};

export type UICustomizationSettingsDialogResult = DialogResult<UICustomizationFieldType>;
export type UICustomizationSettingsDialogProps = DialogProps<UICustomizationSettingsDialogInitValues, UICustomizationSettingsDialogResult>;

const defaultFormValues: UICustomizationFieldType = {
    customizationSettings: undefined
};

export default function UICustomizationSettingsDialog({
    open,
    onClose,
    values,
}: UICustomizationSettingsDialogProps): JSX.Element {

    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();

    const {
        data: customizationSettings,
        status: useStatus
    } = useQueryNotifications(useUICustomizationSettings)({
        userID: values?.userID as number,
        enabled: open
    });

    const {
        mutate: saveCustomizationSettings,
        status: saveStatus
    } = useQueryNotifications(useSaveUICustomizationSettings)({
        userID: values?.userID as number,
        onSuccess: () => {
            acknowledge(t("uiCustomizationSettingsDialog.saveSuccessMessage"));
        }
    });

    const form = useForm<UICustomizationFieldType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        getValues,
        setValue,
        handleSubmit,
        formState: {
            dirtyFields
        },
        reset
    } = form;

    const customizationSettingsField = getValues("customizationSettings");

    // A separate isDirty from the one returned by useForm is implemented
    // here so we can exclude the expanded and formID fields from the data.
    const isDirty = customizationSettingsFieldIsDirty(dirtyFields?.customizationSettings);
    const isValid = customizationSettingsFieldIsValid(customizationSettingsField);

    useEffect(() => {
        if (open) {
            reset(toUICustomizationField(customizationSettings));
        }
    }, [
        reset,
        customizationSettings,
        open
    ]);

    async function onOkay(data: UICustomizationFieldType) {

        const customizationSettings = mergeUICustomizationSettings(data.customizationSettings);

        saveCustomizationSettings(customizationSettings);

        reset();
        onClose?.({ reason: "ok" });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose?.({ reason: "cancel" });
        }
    }
    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function resetToDefault() {
        const customizationSettings = toUICustomizationField(undefined).customizationSettings;
        setValue("customizationSettings", customizationSettings, { shouldDirty: true });
    }

    return (
        <>
            <ResponsiveDialog
                title={t("uiCustomizationSettingsDialog.title")}
                maxWidth="lg"
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    async (reason: string) => {
                        if (reason === "ok") {
                            await handleSubmit(async (data) => onOkay(data))();
                        } else {
                            await onCancel();
                        }
                    }
                }
                paperProps={{
                    sx: {
                        height: "90vh",
                    }
                }}
            // enableAdditionalAction={true}
            // additionalActionLabel={t("buttons.resetToDefault")}
            // onAdditionalAction={resetToDefault}
            >
                <Form
                    form={form}
                    sx={{
                        height: 1,
                        backgroundColor: "background.paper"
                    }}>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 1
                        }}
                    >
                        <FormCheckbox
                            name="customizationSettings.navigationDrawer.openOnHover"
                            label={t("uiCustomizationSettingsDialog.openOnHover")}
                        />
                        <Button onClick={resetToDefault}>
                            {t("buttons.resetToDefault")}
                        </Button>
                    </Stack>
                    <Divider />
                    <UICustomizationViewCategoryListSettings isLoading={useStatus === "loading" || saveStatus === "loading"} />
                </Form>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type UICustomizationSettingsDialogState = DialogState<UICustomizationSettingsDialogInitValues, UICustomizationSettingsDialogResult>;
export type BindUICustomizationSettingsDialogReturn = BindDialogReturn<UICustomizationSettingsDialogInitValues, UICustomizationSettingsDialogResult>;